"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Trophy, Target } from "lucide-react";
import { Flag } from "@/components/shared/flags";
import { motion } from "framer-motion";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface Zapas {
  id: string;
  datum: string;
  cas: string;
  domaci: { nazov: string; kod: string };
  hostia: { nazov: string; kod: string };
  miesto: string;
  jeSlovenskoDomaci: boolean;
  skore_domaci: number | null;
  skore_hosti: number | null;
  stav: string;
}

interface SovenskoZapasyProps {
  zapasy: Zapas[];
}

const HEADER_SECTION_CLASSES = cn(
  "bg-muted/40 px-4 py-3 border-b border-muted/20 flex flex-col sm:flex-row",
  "sm:justify-between sm:items-center text-[10px] font-black uppercase",
  "tracking-[0.15em] text-muted-foreground/80 gap-2 sm:gap-4",
);
const SCORE_CONTAINER_CLASSES = cn(
  "flex flex-col items-center gap-3 px-4 sm:px-6 md:px-8",
  "justify-center min-w-[120px] sm:min-w-[140px]",
);
const TEAM_SECTION_CLASSES = cn(
  "flex-1 text-center space-y-2 min-w-0 group/team",
);
const TEAM_LAYOUT_CLASSES = cn("flex items-center justify-center gap-3");
const FLAG_SIZE = 32;
const CARD_CLASSES = cn(
  "group relative overflow-hidden border-muted/20 bg-transparent rounded-2xl shadow-sm",
  "hover:shadow-xl transition-all duration-300 bg-background/50 backdrop-blur-sm",
);

const TITLE_CLASSES = cn(
  "text-3xl md:text-5xl font-black tracking-tighter uppercase text-foreground drop-shadow-sm",
);

export function SovenskoZapasy({ zapasy }: SovenskoZapasyProps) {
  const SECTION_TITLE = "Zápasy Slovenska";
  const GROUP_LABEL = "Skupina B - Základná časť";
  const SECTION_DESC =
    "Program zápasov slovenskej reprezentácie v základnej skupine. Sledujte cestu nášho tímu za obhajobou bronzovej medaily.";
  const VS_TEXT = "VS";
  const CTA_TEXT = "Pozrieť celkové poradie";

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="text-center space-y-6 mb-12">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Flag kod="SVK" size={40} className="shadow-sm rounded" />
          <h2 className={TITLE_CLASSES}>{SECTION_TITLE}</h2>
          <Flag kod="SVK" size={40} className="shadow-sm rounded" />
        </div>

        <Badge
          variant="outline"
          className="bg-muted/50 text-muted-foreground border-muted/20 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
        >
          <Target className="h-3.5 w-3.5 mr-1.5" />
          {GROUP_LABEL}
        </Badge>

        <p className="text-muted-foreground font-medium max-w-2xl mx-auto leading-relaxed">
          {SECTION_DESC}
        </p>
      </div>

      {/* Zápasy */}
      <div className="space-y-6">
        {zapasy.map((zapas, idx) => (
          <motion.div
            key={zapas.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="group"
          >
            <Link href={`/zapas/${zapas.id}`} className="block">
              <Card className={CARD_CLASSES}>
                <div className={HEADER_SECTION_CLASSES}>
                  <div className="flex items-center gap-2 text-primary font-black">
                    <Trophy className="h-3.5 w-3.5" />
                    {GROUP_LABEL}
                  </div>
                  <div className="flex items-center gap-2 font-bold">
                    <Clock className="h-3.5 w-3.5" />
                    {zapas.datum} o {zapas.cas}
                  </div>
                </div>
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center justify-between gap-4">
                    <div className={TEAM_SECTION_CLASSES}>
                      <div className={TEAM_LAYOUT_CLASSES}>
                        <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                          {zapas.domaci.kod}
                        </div>
                        <Flag kod={zapas.domaci.kod} size={FLAG_SIZE} />
                      </div>
                      <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto">
                        {zapas.domaci.nazov}
                      </div>
                    </div>

                    <div className={SCORE_CONTAINER_CLASSES}>
                      {zapas.stav === "ukonceny" ? (
                        <>
                          <div className="text-3xl sm:text-4xl font-black tracking-tighter tabular-nums">
                            <span
                              className={cn(
                                zapas.skore_domaci !== null &&
                                  zapas.skore_hosti !== null &&
                                  zapas.skore_domaci > zapas.skore_hosti
                                  ? "text-primary"
                                  : "text-foreground",
                              )}
                            >
                              {zapas.skore_domaci}
                            </span>
                            <span className="text-muted-foreground/40 mx-1">
                              :
                            </span>
                            <span
                              className={cn(
                                zapas.skore_domaci !== null &&
                                  zapas.skore_hosti !== null &&
                                  zapas.skore_hosti > zapas.skore_domaci
                                  ? "text-primary"
                                  : "text-foreground",
                              )}
                            >
                              {zapas.skore_hosti}
                            </span>
                          </div>
                          <Badge
                            variant="outline"
                            className="rounded-full px-4 py-1 text-[10px] font-black uppercase tracking-widest bg-status-success/10 text-status-success border-status-success/20"
                          >
                            Koniec
                          </Badge>
                        </>
                      ) : (
                        <>
                          <div className="text-2xl font-black text-muted-foreground/30 italic tracking-widest">
                            {VS_TEXT}
                          </div>
                          <Badge
                            variant="outline"
                            className={cn(
                              "rounded-full px-4 py-1 text-[10px] font-black uppercase tracking-widest",
                              "bg-muted/30 text-muted-foreground border-muted/20",
                            )}
                          >
                            {zapas.stav === "prebieha" ? "LIVE" : "planovaný"}
                          </Badge>
                        </>
                      )}
                    </div>

                    <div className={TEAM_SECTION_CLASSES}>
                      <div className={TEAM_LAYOUT_CLASSES}>
                        <Flag kod={zapas.hostia.kod} size={FLAG_SIZE} />
                        <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                          {zapas.hostia.kod}
                        </div>
                      </div>
                      <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto">
                        {zapas.hostia.nazov}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-muted/10 flex justify-center items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    <MapPin className="h-3.5 w-3.5 text-primary/60" />
                    <span className="truncate">{zapas.miesto}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="flex justify-center pt-12">
        <Button
          asChild
          variant="outline"
          size="lg"
          className="rounded-full px-10 h-12 font-bold uppercase tracking-widest text-[10px] border-muted-foreground/20 hover:bg-muted/50 transition-all"
        >
          <Link href="/skupiny/b">
            <Trophy className="h-4 w-4 mr-2 text-primary" />
            {CTA_TEXT}
          </Link>
        </Button>
      </div>
    </motion.section>
  );
}
